import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronUp, ChevronDown, Copy, User, Image, Bookmark, MessageCircle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AuthModal from '@/components/AuthModal';
import { buildCalcUrl } from '@/utils/promptCostEstimator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PromptCard({ prompt, onVoteChange, onBookmarkChange }) {
  const { user, token } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authHint, setAuthHint] = useState('vote');

  const requireAuth = (hint) => { setAuthHint(hint); setAuthOpen(true); };

  const handleVote = async (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { requireAuth('vote'); return; }
    try {
      const res = await axios.post(`${API}/prompts/${prompt.id}/upvote?direction=${direction}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onVoteChange) onVoteChange(prompt.id, res.data);
    } catch { toast.error('Failed to vote'); }
  };

  const handleBookmark = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { requireAuth('bookmark'); return; }
    try {
      const res = await axios.post(`${API}/prompts/${prompt.id}/bookmark`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onBookmarkChange) onBookmarkChange(prompt.id, res.data);
      toast.success(res.data.bookmarked ? 'Saved' : 'Removed');
    } catch { toast.error('Failed to bookmark'); }
  };

  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(prompt.content);
    toast.success('Prompt copied to clipboard');
  };

  const score = prompt.upvotes || 0;

  return (
    <>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} hint={authHint} />
      <Link
        to={`/prompt/${prompt.slug || prompt.id}`}
        className="block rounded-xl border border-border bg-card hover:-translate-y-1 hover:border-primary/50 transition-all duration-300 group"
        data-testid={`prompt-card-${prompt.id}`}
      >
      <div className="flex">
        {/* Vote column */}
        <div className="flex flex-col items-center py-4 px-2.5 border-r border-border/50 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className={`h-7 w-7 p-0 rounded-md ${prompt.user_vote === 'up' ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-primary'}`}
            onClick={(e) => handleVote(e, 'up')}
            data-testid={`upvote-btn-${prompt.id}`}
          >
            <ChevronUp className="w-5 h-5" strokeWidth={2.5} />
          </Button>
          <span className={`text-xs font-bold my-0.5 tabular-nums ${score > 0 ? 'text-primary' : score < 0 ? 'text-destructive' : 'text-muted-foreground'}`} data-testid={`score-${prompt.id}`}>
            {score}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className={`h-7 w-7 p-0 rounded-md ${prompt.user_vote === 'down' ? 'text-destructive bg-destructive/10' : 'text-muted-foreground hover:text-destructive'}`}
            onClick={(e) => handleVote(e, 'down')}
            data-testid={`downvote-btn-${prompt.id}`}
          >
            <ChevronDown className="w-5 h-5" strokeWidth={2.5} />
          </Button>
          <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground mt-1.5" data-testid={`comment-count-${prompt.id}`}>
            <MessageCircle className="w-3 h-3" /> {prompt.comment_count || 0}
          </span>
          <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground" data-testid={`save-count-${prompt.id}`}>
            <Bookmark className="w-3 h-3" /> {prompt.bookmark_count || 0}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 overflow-hidden p-4">
          {/* Categories */}
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            {prompt.categories?.map(cat => (
              <Badge key={cat} variant="secondary" className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 text-xs font-medium">
                {cat}
              </Badge>
            ))}
          </div>

          {/* Title */}
          <h3 className="text-base font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2" data-testid={`prompt-title-${prompt.id}`}>
            {prompt.title}
          </h3>

          {/* Content preview */}
          <p className="text-sm text-muted-foreground line-clamp-3 mb-3 leading-relaxed font-mono break-words">
            {prompt.content}
          </p>

          {/* Footer */}
          <div className="flex items-center justify-between pt-2.5 border-t border-border/50">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className={`h-7 px-2 rounded-full text-xs ${prompt.has_bookmarked ? 'text-amber-500 bg-amber-500/10' : 'text-muted-foreground'}`}
                onClick={handleBookmark}
                data-testid={`bookmark-btn-${prompt.id}`}
              >
                <Bookmark className={`w-3.5 h-3.5 ${prompt.has_bookmarked ? 'fill-current' : ''}`} />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 rounded-full text-xs text-muted-foreground" onClick={handleCopy} data-testid={`copy-btn-${prompt.id}`}>
                <Copy className="w-3.5 h-3.5" /> Copy
              </Button>
              <Link
                to={buildCalcUrl(prompt)}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center justify-center h-7 w-7 rounded-full text-xs text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                title="Calculate cost"
                data-testid={`calc-cost-btn-${prompt.id}`}
              >
                <DollarSign className="w-3.5 h-3.5" />
              </Link>
              {prompt.screenshot_count > 0 && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Image className="w-3.5 h-3.5" /> {prompt.screenshot_count}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0 overflow-hidden">
              <User className="w-3 h-3 shrink-0" />
              <span className="truncate">{prompt.username}</span>
            </div>
          </div>
        </div>
      </div>
      </Link>
    </>
  );
}
